import { Alert, Button, Card, Checkbox, Input, Modal, Space, Table, message } from "antd";
import moment from "moment";
import { useCallback, useEffect, useRef, useState } from "react";
import { v4 as uuid } from "uuid";
import {
  getWarehouseContext,
  stockOutVehicle,
  WarehouseContext,
  WarehouseHistory,
  WarehouseStock,
  WarehouseStockOutRequest,
} from "../service/warehouse.service";

interface Props {
  vehicleId: string;
  reloadToken: boolean;
  onSuccess: () => void;
}

const time = (value?: number | null) => value == null ? "-" : moment(value).format("YYYY-MM-DD HH:mm:ss");
const camp = (row: { campId: number; campName?: string | null }) => row.campName || `창고 #${row.campId}`;
const errorMessage = (error: any, fallback: string) => error?.response?.data?.message || fallback;

export function VehicleWarehouseCard({ vehicleId, reloadToken, onSuccess }: Props) {
  const [context, setContext] = useState<WarehouseContext | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [visible, setVisible] = useState(false);
  const [stockIds, setStockIds] = useState<number[]>([]);
  const [reason, setReason] = useState("");
  const [physicalConfirmed, setPhysicalConfirmed] = useState(false);
  const [correctionConfirmed, setCorrectionConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pendingRequest, setPendingRequest] = useState<WarehouseStockOutRequest | null>(null);
  const [submitError, setSubmitError] = useState("");
  const generation = useRef(0);
  const inFlight = useRef(false);

  const reload = useCallback(async () => {
    const current = ++generation.current;
    inFlight.current = false;
    setSubmitting(false);
    setVisible(false);
    setContext(null);
    setLoadError("");
    setLoading(true);
    setPendingRequest(null);
    setSubmitError("");
    try {
      const result = await getWarehouseContext(vehicleId);
      if (current === generation.current) {
        setContext(result);
      }
    } catch (error) {
      if (current === generation.current) setLoadError(errorMessage(error, "입출고 상태를 조회하지 못했습니다"));
    } finally {
      if (current === generation.current) setLoading(false);
    }
  }, [vehicleId]);

  useEffect(() => {
    reload();
    return () => { generation.current += 1; };
  }, [reload, reloadToken]);

  const showStockOutAction = context?.status === "Cargo" || Boolean(context?.stocks.length);
  const selectableStocks = context?.stocks.filter((row) => row.selectable) || [];
  const selectedStocks = selectableStocks.filter((row) => stockIds.includes(row.stockId));
  const deviatesFromSuggestion = context != null && (
    stockIds.length !== context.suggestedStockIds.length ||
    stockIds.some((id) => !context.suggestedStockIds.includes(id))
  );
  const needsCorrection = context?.correctionRequired || deviatesFromSuggestion;
  const valid = context != null && context.vehicleId === vehicleId && context.canStockOut && !context.riding && context.assetNo != null &&
    (selectableStocks.length === 0 || selectedStocks.length > 0) &&
    physicalConfirmed && reason.trim().length > 0 && reason.trim().length <= 500 &&
    (!needsCorrection || correctionConfirmed);

  function open() {
    if (!context) return;
    if (pendingRequest) {
      setVisible(true);
      return;
    }
    setStockIds(context.suggestedStockIds);
    setReason("");
    setPhysicalConfirmed(false);
    setCorrectionConfirmed(false);
    setPendingRequest(null);
    setSubmitError("");
    setVisible(true);
  }

  async function submit() {
    if (!context || !valid || inFlight.current) return;
    const current = generation.current;
    const request = pendingRequest || {
      requestId: uuid(),
      expectedRevision: context.revision,
      assetNo: context.assetNo!,
      stockIds: selectedStocks.map((row) => row.stockId),
      expectedHistoryIds: [...new Set(selectedStocks.flatMap((row) => row.matchedHistoryId == null ? [] : [row.matchedHistoryId]))],
      physicalConfirmed,
      correctionConfirmed: Boolean(needsCorrection && correctionConfirmed),
      reason: reason.trim(),
    };
    inFlight.current = true;
    setSubmitting(true);
    setPendingRequest(request);
    setSubmitError("");
    try {
      const result = await stockOutVehicle(vehicleId, request);
      if (current !== generation.current) return;
      message.success(result.unresolvedHistoryIds.length > 0
        ? `출고 처리했습니다. 미확정 입고 기록 ${result.unresolvedHistoryIds.length}건은 보존했습니다`
        : "출고 처리했습니다");
      setVisible(false);
      onSuccess();
    } catch (error: any) {
      if (current !== generation.current) return;
      if (error?.response?.status === 409) {
        message.warning(`${errorMessage(error, "현재 상태가 변경되었거나 이미 처리된 요청입니다")}. 상태를 다시 확인해 주세요`);
        setVisible(false);
        onSuccess();
      } else {
        setSubmitError(errorMessage(error, "처리 결과를 확인하지 못했습니다. 같은 요청으로 재시도하거나 상태를 새로 조회해 주세요"));
      }
    } finally {
      if (current === generation.current) {
        inFlight.current = false;
        setSubmitting(false);
      }
    }
  }

  const stockColumns = [
    { title: "재고 번호", dataIndex: "stockId" },
    { title: "창고", render: (_: unknown, row: WarehouseStock) => camp(row) },
    { title: "자산번호", dataIndex: "assetNo", render: (value: number | null) => value ?? "-" },
    { title: "입고 당시 QR", dataIndex: "qr" },
    { title: "입고 시각", dataIndex: "incomeAt", render: time },
    { title: "연결 입고 번호", dataIndex: "matchedHistoryId", render: (value?: number | null) => value ?? "미확정" },
  ];

  return (
    <Card
      title="입출고 상태"
      style={{ margin: "24px 0" }}
      loading={loading}
      extra={<Button onClick={reload} disabled={loading || submitting}>새로 조회</Button>}
    >
      {loadError && <Alert type="error" showIcon message={loadError} />}
      {context && <Space direction="vertical" style={{ width: "100%" }} size="middle">
        {context.issues
          .filter((issue) => issue.code !== "NO_SELECTABLE_STOCK" && issue.code !== "DEER_CDC_AUTHORITATIVE")
          .map((issue, index) => (
            <Alert key={`${issue.code}-${index}`} type="warning" showIcon message={issue.message} />
          ))}
        <div>
          <h4>현재 재고</h4>
          <Table<WarehouseStock>
            size="small"
            rowKey="stockId"
            columns={stockColumns}
            dataSource={context.stocks}
            pagination={false}
            scroll={{ x: 700 }}
            locale={{ emptyText: "현재 재고가 없습니다" }}
          />
        </div>
        <div>
          <h4>관련 입고 기록</h4>
          <Table<WarehouseHistory>
            size="small"
            rowKey="historyId"
            dataSource={context.histories}
            pagination={{ pageSize: 5, hideOnSinglePage: true }}
            scroll={{ x: 700 }}
            locale={{ emptyText: "관련 입고 기록이 없습니다" }}
            columns={[
              { title: "입고 번호", dataIndex: "historyId" },
              { title: "창고", render: (_: unknown, row: WarehouseHistory) => camp(row) },
              { title: "자산번호", dataIndex: "assetNo", render: (value) => value ?? "-" },
              { title: "입고 당시 QR", dataIndex: "qr" },
              { title: "입고 시각", dataIndex: "incomeAt", render: time },
              { title: "출고 시각", dataIndex: "outgoAt", render: time },
              { title: "연결 재고 번호", dataIndex: "matchedStockId", render: (value) => value ?? "미확정" },
            ]}
          />
        </div>
        {showStockOutAction && <Button
          type="primary"
          onClick={open}
          disabled={!context.canStockOut || context.riding || context.assetNo == null || submitting}
        >
          출고 처리
        </Button>}
      </Space>}
      <Modal
        title="기기 출고 확인"
        visible={visible}
        width={860}
        onOk={submit}
        onCancel={() => !submitting && setVisible(false)}
        maskClosable={false}
        closable={!submitting}
        okText={pendingRequest ? "같은 요청 재시도" : "출고 확정"}
        cancelText="취소"
        confirmLoading={submitting}
        okButtonProps={{ disabled: !valid }}
        cancelButtonProps={{ disabled: submitting }}
      >
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Alert type="info" message="실물 기기와 현재 재고를 확인한 뒤 출고해 주세요" />
          <Table<WarehouseStock>
            size="small"
            rowKey="stockId"
            columns={stockColumns}
            dataSource={context?.stocks || []}
            pagination={false}
            scroll={{ x: 700 }}
            locale={{ emptyText: "재고 없이 기기 상태만 보정합니다" }}
            rowSelection={{
              selectedRowKeys: stockIds,
              onChange: (keys) => {
                setStockIds(keys.map(Number));
                setCorrectionConfirmed(false);
              },
              getCheckboxProps: (row) => ({ disabled: !row.selectable || submitting || pendingRequest != null }),
            }}
          />
          {context?.correctionRequired && <Alert type="warning" showIcon message={selectableStocks.length === 0
            ? "입고 재고가 확인되지 않아 기기 상태만 출고됨으로 변경합니다"
            : "입고 기록과 재고가 일치하지 않습니다. 선택한 재고만 제거하며, 확정할 수 없는 입고 기록은 보존합니다"} />}
          {deviatesFromSuggestion && <Alert type="warning" showIcon message="제안된 재고 선택을 변경했습니다. 선택한 재고만 출고하고 나머지는 보존합니다" />}
          <Input.TextArea
            aria-label="출고 사유"
            placeholder="출고 사유 (필수, 최대 500자)"
            value={reason}
            maxLength={500}
            rows={3}
            disabled={submitting || pendingRequest != null}
            onChange={(event) => setReason(event.target.value)}
          />
          <Checkbox
            checked={physicalConfirmed}
            disabled={submitting || pendingRequest != null}
            onChange={(event) => setPhysicalConfirmed(event.target.checked)}
          >
            실물 기기와 자산번호를 확인했고 출고를 진행합니다
          </Checkbox>
          {needsCorrection && <Checkbox
            checked={correctionConfirmed}
            disabled={submitting || pendingRequest != null}
            onChange={(event) => setCorrectionConfirmed(event.target.checked)}
          >
            미확정 입고 기록을 보존하고 선택한 재고·기기 상태를 보정하는 데 동의합니다
          </Checkbox>}
          {submitError && <>
            <Alert type="error" message={submitError} />
            <Button onClick={reload} disabled={submitting}>현재 상태 다시 조회</Button>
          </>}
        </Space>
      </Modal>
    </Card>
  );
}
